/**
 * Attach / move / detach a DEPLOYMENT (cloud tenant or on-prem license) to a Organizzazione —
 * ADR-0076 fetta 2.
 *
 * The routes existed since the Organizzazione shipped and NO page called them, so a link could
 * only ever be set at creation time and never corrected. That made the model unusable in
 * practice: decision 9 accepts that duplicate Organizzazioni happen and are cured AFTERWARDS by
 * moving the deployments and archiving the empty record — which needs exactly this dialog.
 *
 * One component for both kinds because the operation is identical (`organization_id: null`
 * DETACHES on both routes); only the label and the mutation differ.
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { platformApi } from "@/lib/platform";

/** Sentinel for "no organization" — an empty string is the unset select value, and the two
 * must stay distinguishable so DETACH is an explicit choice, not a blank submit. */
export const NO_ORGANIZATION = "__none__";

export type LinkTarget = { kind: "tenant" | "license"; id: string; label: string };

export function OrganizationLinkDialog({
  target,
  currentOrganizationId,
  onClose,
}: {
  target: LinkTarget;
  currentOrganizationId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [choice, setChoice] = React.useState(currentOrganizationId ?? NO_ORGANIZATION);
  const [error, setError] = React.useState<string | null>(null);

  const organizationsQ = useQuery({
    queryKey: ["platform", "organizations", { search: "", status: "active" }],
    queryFn: () => platformApi.listOrganizations({ status: "active" }),
  });

  // ONE control, not a search box next to a native <select>: the Combobox carries its own
  // search, and the legal name rides as the option's second line instead of being crammed
  // into the label with a dash. Detach is the first option, so it stays an explicit pick.
  const options = React.useMemo(() => {
    const rows = organizationsQ.data ?? [];
    const out = [
      { value: NO_ORGANIZATION, label: "— Nessuna (sgancia) —" },
      ...rows.map((c) => ({
        value: c.id,
        label: c.name,
        description: c.legal_name && c.legal_name !== c.name ? c.legal_name : undefined,
      })),
    ];
    // The list is filtered to ACTIVE organizations, so a deployment attached to an ARCHIVED
    // one would open on a value that isn't an option — the control would read empty and the
    // operator would think the link was already gone. Keep the current one addressable
    // (labelled for what it is) so this dialog can only ever MOVE it deliberately.
    const current = currentOrganizationId;
    if (current && !rows.some((c) => c.id === current)) {
      out.splice(1, 0, { value: current, label: "Organizzazione attuale (archiviata)" });
    }
    return out;
  }, [organizationsQ.data, currentOrganizationId]);

  const save = useMutation({
    mutationFn: async () => {
      const organizationId = choice === NO_ORGANIZATION ? null : choice;
      if (target.kind === "tenant") {
        await platformApi.setTenantOrganization(target.id, organizationId);
      } else {
        await platformApi.setLicenseOrganization(target.id, organizationId);
      }
    },
    onSuccess: async () => {
      // Both sides of the link go stale: the deployment's own page AND every organization
      // view (the counts + the detail lists move with it). Keys must match the real
      // ones — TanStack matches by PREFIX, so `["platform","tenants"]` covers the list
      // AND the `["platform","tenants",id]` detail.
      await qc.invalidateQueries({ queryKey: ["platform", "organizations"] });
      await qc.invalidateQueries({ queryKey: ["platform", "tenants"] });
      await qc.invalidateQueries({ queryKey: ["platform", "licenses"] });
      onClose();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Operazione non riuscita"),
  });

  const unchanged = choice === (currentOrganizationId ?? NO_ORGANIZATION);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Organizzazione — {target.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {target.kind === "tenant"
              ? "A quale azienda appartiene questo workspace."
              : "A quale azienda appartiene questa installazione on-prem."}{" "}
            Spostarlo è solo un cambio di attribuzione: non tocca il deployment.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="cl-organization">Organizzazione</Label>
            <Combobox
              id="cl-organization"
              options={options}
              value={choice}
              onChange={(v) => setChoice(v ?? NO_ORGANIZATION)}
              loading={organizationsQ.isLoading}
              placeholder="Scegli un'organizzazione…"
              searchPlaceholder="Nome o ragione sociale…"
              emptyText="Nessuna organizzazione trovata"
            />
          </div>
          {error && <p className="text-sm text-danger">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => save.mutate()} disabled={unchanged || save.isPending}>
            {save.isPending ? "Salvo…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
